import { PricePlan } from 'common/types/OfferInterface'
import React from 'react'
import {
  OfferLineDataTestProps,
  OfferLineItem,
  OfferLineUiProps,
} from 'src/shared/components/offer-price/offer-line-item/offer-line-item'
import { ProductWrapper } from 'src/shared/components/offer-price/physical-product/ui/product-wrapper'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import { checkIsTaxInclusive } from 'src/shared/utils/price-calculator'
import {
  getPriceFormatter,
  isLimitedSubscriptionPlan,
  isOneShot,
  isSubscriptionPlan,
} from 'src/shared/utils/price-plan-utils'

export interface ProductProps extends OfferLineDataTestProps {
  pricePlan?: PricePlan
  offerPricing?: CalculatePriceResponse
  offerLineUiProps: OfferLineUiProps
  isSelected?: boolean
  onSelect?: () => void
  locale: string
}

const COUPON_COLOR = 'rgba(34, 139, 34)'

export const DigitalProduct = ({
  offerPricing,
  pricePlan,
  locale,
  isSelected = false,
  onSelect,
  offerLineUiProps,
  dataTestIdName,
  dataTestIdPrice,
  dataTestIdCurrency,
  dataTestIdTax,
}: ProductProps) => {
  const { t } = useTypedTranslation(['common'])
  const isTaxInclusive = !!pricePlan && checkIsTaxInclusive(pricePlan)
  const shouldDisplayTax = !isTaxInclusive && !!offerPricing?.tax.amount
  const isDiscountApplied =
    !!offerPricing?.discount.amount && offerPricing?.discount.amount > 0
  const shouldDisplaySubtotal =
    (isSubscriptionPlan(pricePlan) || isOneShot(pricePlan)) &&
    (shouldDisplayTax || isDiscountApplied)
  const priceFormatter = getPriceFormatter(pricePlan, t)

  return (
    <ProductWrapper>
      <OfferLineItem
        onSelect={onSelect}
        isSelected={isSelected}
        label={pricePlan?.name}
        priceFormatter={priceFormatter}
        amount={offerPricing?.originalAmount}
        currency={offerPricing?.currencyCode}
        tax={isTaxInclusive ? offerPricing?.tax : undefined}
        locale={locale}
        dataTestIdName={`${dataTestIdName}-price`}
        dataTestIdPrice={`${dataTestIdPrice}-price`}
        dataTestIdCurrency={`${dataTestIdCurrency}-price`}
        dataTestIdTax={`${dataTestIdTax}-price`}
        {...offerLineUiProps}
      />
      {isSelected && (
        <>
          {isDiscountApplied && (
            <OfferLineItem
              label={t('components.offer_price.coupon')}
              amount={-offerPricing?.discount.amount}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={`${dataTestIdName}-coupon`}
              dataTestIdPrice={`${dataTestIdPrice}-coupon`}
              dataTestIdCurrency={`${dataTestIdCurrency}-coupon`}
              dataTestIdTax={`${dataTestIdTax}-coupon`}
              {...offerLineUiProps}
              nameColor={COUPON_COLOR}
              mobileNameColor={COUPON_COLOR}
              amountColor={COUPON_COLOR}
              mobileAmountColor={COUPON_COLOR}
            />
          )}
          {shouldDisplayTax && (
            <OfferLineItem
              label={`${t('components.offer_price.vat')} (${offerPricing?.tax.rate}%)`}
              amount={offerPricing?.tax.amount}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={`${dataTestIdName}-inclusive`}
              dataTestIdPrice={`${dataTestIdPrice}-inclusive`}
              dataTestIdCurrency={`${dataTestIdCurrency}-inclusive`}
              dataTestIdTax={`${dataTestIdTax}-inclusive`}
              {...offerLineUiProps}
            />
          )}
          {shouldDisplaySubtotal && (
            <OfferLineItem
              label={t('components.offer_price.subtotal')}
              priceFormatter={priceFormatter}
              amount={offerPricing?.grossAmount}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={`${dataTestIdName}-subtotal`}
              dataTestIdPrice={`${dataTestIdPrice}-subtotal`}
              dataTestIdCurrency={`${dataTestIdCurrency}-subtotal`}
              dataTestIdTax={`${dataTestIdTax}-subtotal`}
              {...offerLineUiProps}
            />
          )}
          {isLimitedSubscriptionPlan(pricePlan) && (
            <OfferLineItem
              label={t('components.offer_price.due_today')}
              amount={offerPricing?.dueToday}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={`${dataTestIdName}-due-today`}
              dataTestIdPrice={`${dataTestIdPrice}-due-today`}
              dataTestIdCurrency={`${dataTestIdCurrency}-due-today`}
              dataTestIdTax={`${dataTestIdTax}-due-today`}
              {...offerLineUiProps}
            />
          )}
          {isLimitedSubscriptionPlan(pricePlan) && (
            <OfferLineItem
              label={t('components.offer_price.order_total')}
              amount={offerPricing?.orderTotal}
              currency={offerPricing?.currencyCode}
              locale={locale}
              dataTestIdName={dataTestIdName}
              dataTestIdPrice={dataTestIdPrice}
              dataTestIdCurrency={dataTestIdCurrency}
              dataTestIdTax={dataTestIdTax}
              {...offerLineUiProps}
            />
          )}
        </>
      )}
    </ProductWrapper>
  )
}
